import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import {
  FileText, Download, Eye, EyeOff, Loader2, Search, Lock,
  Calendar as CalendarIcon, DollarSign, CheckSquare, Download as DownloadIcon
} from 'lucide-react';
import { payrollApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

interface SalarySlip {
  id: string;
  employee_id: string;
  employee_name: string;
  month: string; // YYYY-MM format
  slip_type: 'salary' | 'bonus' | 'allowance' | 'claims';
  gross_pay: number;
  net_pay: number;
  deductions: number;
  payment_date: string;
  is_password_protected: boolean;
  status: string;
}

const SalarySlipsNew: React.FC = () => {
  const { toast } = useToast();

  const [selectedSlip, setSelectedSlip] = useState<SalarySlip | null>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [showAmounts, setShowAmounts] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState<string>('all-months');
  const [selectedSlipType, setSelectedSlipType] = useState<string>('all');
  const [selectedSlips, setSelectedSlips] = useState<string[]>([]);
  const [password, setPassword] = useState('');
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);

  // Current user (in real app, get from auth)
  const currentUserId = 'emp-1';

  // Fetch salary slips from API
  const { data: slips = [], isLoading, error } = useQuery({
    queryKey: ['salary-slips', selectedMonth, selectedSlipType],
    queryFn: async () => {
      const params: any = {};
      if (selectedMonth && selectedMonth !== 'all-months') {
        params.month = selectedMonth;
      }
      if (selectedSlipType && selectedSlipType !== 'all') {
        params.slip_type = selectedSlipType;
      }
      const response = await payrollApi.salarySlips.getAll(params);
      return response as SalarySlip[];
    },
  });

  // Filter slips by search term (month and type already filtered by API)
  const filteredSlips = slips.filter(slip => {
    if (!searchTerm) return true;
    const empName = slip.employee_name || '';
    return empName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleViewSlip = (slip: SalarySlip) => {
    if (slip.is_password_protected) {
      setSelectedSlip(slip);
      setIsPasswordDialogOpen(true);
    } else {
      setSelectedSlip(slip);
      setIsViewOpen(true);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!selectedSlip) return;

    try {
      const result = await payrollApi.salarySlips.verifyPassword(selectedSlip.id, password) as any;

      if (result.verified) {
        setIsPasswordDialogOpen(false);
        setIsViewOpen(true);
        setPassword('');
        toast({ title: 'Password verified', description: 'Accessing salary slip...' });
      } else {
        toast({
          title: 'Invalid password',
          description: result.message || 'Please enter the correct password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Verification failed',
        description: 'Unable to verify password. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleDownloadSlip = async (slipId: string) => {
    try {
      toast({ title: 'Downloading slip...', description: 'Your salary slip is being downloaded' });
      await payrollApi.salarySlips.download(slipId);
      toast({ title: 'Download complete', description: 'Salary slip downloaded successfully' });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Unable to download salary slip. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const handleBulkDownload = async () => {
    if (selectedSlips.length === 0) {
      toast({
        title: 'No slips selected',
        description: 'Please select slips to download',
        variant: 'destructive'
      });
      return;
    }

    try {
      toast({
        title: `Downloading ${selectedSlips.length} slips...`,
        description: 'Your salary slips are being prepared for download'
      });
      await payrollApi.salarySlips.bulkDownload(selectedSlips);
      toast({
        title: 'Download complete',
        description: `${selectedSlips.length} salary slips downloaded successfully`
      });
      setSelectedSlips([]);
    } catch (error) {
      toast({
        title: 'Download failed',
        description: 'Unable to download salary slips. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const toggleSlipSelection = (slipId: string) => {
    setSelectedSlips(prev =>
      prev.includes(slipId)
        ? prev.filter(id => id !== slipId)
        : [...prev, slipId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedSlips.length === filteredSlips.length) {
      setSelectedSlips([]);
    } else {
      setSelectedSlips(filteredSlips.map(s => s.id));
    }
  };

  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const formatCurrency = (amount: number) => {
    return `$${amount.toLocaleString()}`;
  };

  const getSlipTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      salary: 'bg-blue-50 text-blue-700 border-blue-200/60 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800/50',
      bonus: 'bg-indigo-50 text-indigo-700 border-indigo-200/60 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800/50',
      allowance: 'bg-purple-50 text-purple-700 border-purple-200/60 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-800/50',
      claims: 'bg-rose-50 text-rose-700 border-rose-200/60 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-800/50',
    };

    const classNames = colors[type] || 'bg-slate-50 text-slate-700 border-slate-200';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none capitalize shrink-0", classNames)} variant="outline">
        {type}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    const isPaid = status.toUpperCase() === 'PAID';
    return (
      <Badge className={cn("px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0", isPaid ? "bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/50" : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-400 dark:border-slate-700/50")} variant="outline">
        {status}
      </Badge>
    );
  };

  // Generate months for dropdown
  const generateMonths = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthStr = date.toISOString().slice(0, 7);
      months.push(monthStr);
    }
    return months;
  };

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">View and download your salary slips</h2>
        </div>
        <div className="flex flex-wrap items-center gap-3 self-start md:self-auto">
          {/* Show/Hide Amounts Toggle */}
          <div className="flex items-center space-x-2 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-1.5 bg-white dark:bg-slate-900 shadow-sm shrink-0">
            <Label className="text-xs font-bold text-slate-500 tracking-wide">Show Amounts</Label>
            <Switch
              checked={showAmounts}
              onCheckedChange={setShowAmounts}
              className="data-[state=checked]:bg-[#143A62]"
            />
          </div>
          {/* Bulk Download */}
          {selectedSlips.length > 0 && (
            <Button 
              onClick={handleBulkDownload} 
              variant="outline"
            >
              <DownloadIcon className="h-4 w-4 mr-1.5 shrink-0" />
              <span>Download ({selectedSlips.length})</span>
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by employee name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-xs h-9 shadow-sm"
          />
        </div>

        {/* Month Filter */}
        <div className="flex items-center space-x-2 shrink-0">
          <CalendarIcon className="h-4 w-4 text-slate-400" />
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[180px] rounded-xl shadow-sm">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent className="border-slate-105 dark:border-slate-800">
              <SelectItem value="all-months" className="text-xs font-bold">All Months</SelectItem>
              {generateMonths().map(month => (
                <SelectItem key={month} value={month} className="text-xs font-bold">
                  {formatMonth(month)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Slip Type Filter */}
        <div className="shrink-0">
          <Select value={selectedSlipType} onValueChange={setSelectedSlipType}>
            <SelectTrigger className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 focus:ring-0 focus:ring-offset-0 font-bold text-xs h-9 w-[160px] rounded-xl shadow-sm">
              <SelectValue placeholder="Select slip type" />
            </SelectTrigger>
            <SelectContent className="border-slate-105 dark:border-slate-800">
              <SelectItem value="all" className="text-xs font-bold">All Types</SelectItem>
              <SelectItem value="salary" className="text-xs font-bold">Salary</SelectItem>
              <SelectItem value="bonus" className="text-xs font-bold">Bonus</SelectItem>
              <SelectItem value="allowance" className="text-xs font-bold">Allowance</SelectItem>
              <SelectItem value="claims" className="text-xs font-bold">Claims</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Salary Slips Table */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50 flex flex-row items-center justify-between space-y-0 gap-4 flex-wrap">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Your Salary Slips</CardTitle>
          {filteredSlips.length > 0 && (
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={selectedSlips.length === filteredSlips.length}
                onCheckedChange={toggleSelectAll}
                className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-[#143A62] dark:data-[state=checked]:bg-blue-600"
              />
              <Label className="text-xs font-bold text-slate-450 tracking-wider uppercase cursor-pointer" onClick={toggleSelectAll}>
                Select All
              </Label>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="w-12 px-8">
                  <span className="sr-only">Select</span>
                </TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Salary Month</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Type</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Gross Pay</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Deductions</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Net Pay</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Payment Date</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 text-right px-8">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-20 px-8">
                    <Loader2 className="h-6 w-6 animate-spin text-blue-650 mx-auto" />
                    <p className="text-slate-400 font-bold text-xs mt-2">Loading salary slips...</p>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-20 px-8 text-rose-600 font-semibold text-xs">
                    Error loading salary slips. Please try again.
                  </TableCell>
                </TableRow>
              ) : filteredSlips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-20 px-8 text-slate-400 font-semibold text-xs animate-pulse">
                    No salary slips found. Adjust your filters to see more results.
                  </TableCell>
                </TableRow>
              ) : (
                filteredSlips.map((slip) => (
                  <TableRow key={slip.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-850/50">
                    <TableCell className="px-8 py-4">
                      <Checkbox
                        checked={selectedSlips.includes(slip.id)}
                        onCheckedChange={() => toggleSlipSelection(slip.id)}
                        className="rounded-md border-slate-300 dark:border-slate-700 data-[state=checked]:bg-[#143A62] dark:data-[state=checked]:bg-blue-600"
                      />
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center space-x-2">
                        <CalendarIcon className="h-4 w-4 text-blue-500 shrink-0" />
                        <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{formatMonth(slip.month)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="py-4">{getSlipTypeBadge(slip.slip_type)}</TableCell>
                    <TableCell className="text-xs font-bold text-slate-700 dark:text-slate-300 py-4">
                      {showAmounts ? (
                        <span>{formatCurrency(slip.gross_pay)}</span>
                      ) : (
                        <span className="text-slate-400 font-normal">••••••</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-bold text-rose-600 dark:text-rose-400 py-4">
                      {showAmounts ? (
                        <span>
                          -{formatCurrency(slip.deductions)}
                        </span>
                      ) : (
                        <span className="text-slate-400 font-normal">••••••</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-black text-emerald-600 dark:text-emerald-400 py-4">
                      {showAmounts ? (
                        <span>
                          {formatCurrency(slip.net_pay)}
                        </span>
                      ) : (
                        <span className="text-slate-400 font-normal">••••••</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs font-semibold text-slate-500 py-4">
                      {new Date(slip.payment_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(slip.status)}
                        {slip.is_password_protected && (
                          <Lock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="px-8 py-4 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleViewSlip(slip)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadSlip(slip.id)}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Password Dialog */}
      <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
        <DialogContent className="w-[min(96vw,500px)] max-w-[500px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center space-x-2">
              <Lock className="h-5 w-5 text-orange-500 shrink-0" />
              <span>Password Protected Slip</span>
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              This salary slip is password protected. Please enter your password to view.
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-bold text-slate-400 tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                className="rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-bold text-sm h-11"
              />
              <p className="text-[10px] font-bold text-slate-400 mt-1 leading-normal tracking-wide">
                Hint: Your password is the last 4 digits of your phone number
              </p>
            </div>
          </div>

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePasswordSubmit}>
              Verify & View
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slip View Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="w-[min(96vw,650px)] max-w-[650px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 overflow-hidden shadow-2xl gap-0">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100">Salary Slip Details</DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              {selectedSlip && `${formatMonth(selectedSlip.month)} - ${selectedSlip.slip_type.toUpperCase()}`}
            </DialogDescription>
          </DialogHeader>

          {selectedSlip && (
            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              {/* Header Info */}
              <div className="grid grid-cols-2 gap-4 p-5 bg-slate-50/60 dark:bg-slate-800/20 border border-slate-100 dark:border-slate-800/50 rounded-2xl">
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Employee</Label>
                  <p className="font-bold text-xs text-slate-750 dark:text-slate-350 mt-1">{selectedSlip.employee_name}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Salary Month</Label>
                  <p className="font-bold text-xs text-blue-600 mt-1">{formatMonth(selectedSlip.month)}</p>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Slip Type</Label>
                  <div className="mt-1">{getSlipTypeBadge(selectedSlip.slip_type)}</div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold text-slate-400 tracking-wider uppercase">Payment Date</Label>
                  <p className="font-bold text-xs text-slate-750 dark:text-slate-350 mt-1">{new Date(selectedSlip.payment_date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Earnings & Deductions */}
              <div className="space-y-4">
                <div className="border-b border-slate-100 dark:border-slate-800 pb-2">
                  <h3 className="font-bold text-sm text-[#143A62] dark:text-slate-200">Earnings</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-500">
                    <span>Basic Salary</span>
                    <span className="font-bold text-slate-700 dark:text-slate-300">{formatCurrency(selectedSlip.gross_pay * 0.7)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-500">
                    <span>Allowances</span>
                    <span className="font-bold text-slate-700 dark:text-slate-300">{formatCurrency(selectedSlip.gross_pay * 0.3)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-2 text-xs">
                    <span className="font-bold text-slate-800 dark:text-slate-200">Gross Pay</span>
                    <span className="font-black text-sm text-[#143A62] dark:text-blue-450">{formatCurrency(selectedSlip.gross_pay)}</span>
                  </div>
                </div>

                <div className="border-b border-slate-100 dark:border-slate-800 pb-2 mt-6">
                  <h3 className="font-bold text-sm text-rose-600">Deductions</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-500">
                    <span>Tax</span>
                    <span className="font-bold text-rose-600">-{formatCurrency(selectedSlip.deductions * 0.6)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-500">
                    <span>Insurance</span>
                    <span className="font-bold text-rose-600">-{formatCurrency(selectedSlip.deductions * 0.4)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-slate-100 dark:border-slate-800 pt-2 text-xs">
                    <span className="font-bold text-rose-800 dark:text-rose-200">Total Deductions</span>
                    <span className="font-black text-sm text-rose-600">-{formatCurrency(selectedSlip.deductions)}</span>
                  </div>
                </div>

                {/* Net Pay */}
                <div className="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/20 rounded-2xl p-4 mt-6 shadow-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-bold text-emerald-800 dark:text-emerald-300">Net Pay</span>
                    <span className="text-2xl font-black text-emerald-600 dark:text-emerald-450">
                      {formatCurrency(selectedSlip.net_pay)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0 flex gap-2">
            <Button variant="outline" onClick={() => setIsViewOpen(false)}>
              Close
            </Button>
            <Button onClick={() => selectedSlip && handleDownloadSlip(selectedSlip.id)}>
              <Download className="h-4 w-4 shrink-0" />
              <span>Download PDF</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalarySlipsNew;
